import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase, SUPABASE_ANON_KEY } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { PageHeader, EmptyState, Modal } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── Helpers ───────────────────────────────────────────────────────────────────
const daysUntil = dateStr => {
  if (!dateStr) return null
  const diff = Math.floor((new Date(dateStr) - new Date()) / 86400000)
  return diff
}

function DueDateBadge({ dateStr, isDone }) {
  if (!dateStr) return null
  const days = daysUntil(dateStr)
  if (isDone) return (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#dcfce7', color: '#22c55e', fontWeight: 700 }}>
      📅 {formatDate(dateStr)}
    </span>
  )
  if (days < 0) return (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#fee2e2', color: '#ef4444', fontWeight: 700, border: '1px solid #fca5a5' }}>
      🔴 已逾期 {Math.abs(days)} 天
    </span>
  )
  if (days === 0) return (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#fef9c3', color: '#ca8a04', fontWeight: 700, border: '1px solid #fde047' }}>
      ⚠️ 今天到期
    </span>
  )
  if (days <= 3) return (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#fff7ed', color: '#f97316', fontWeight: 700, border: '1px solid #fed7aa' }}>
      ⏰ 還剩 {days} 天
    </span>
  )
  if (days <= 7) return (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#fef9c3', color: '#ca8a04', fontWeight: 700 }}>
      📅 還剩 {days} 天
    </span>
  )
  return (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#f1f5f9', color: '#64748b', fontWeight: 600 }}>
      📅 {formatDate(dateStr)}
    </span>
  )
}

function exportCSV(filename, headers, rows) {
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

// ── Task form (reused for create + edit) ──────────────────────────────────────
function TaskFormBody({ form, setForm, users, categories, priorities, _ }) {
  const [userSearch, setUserSearch] = useState('')
  const depts = useMemo(() => [...new Set(users.map(u => u.department || '未分部門'))].sort(), [users])
  const filteredUsers = useMemo(() =>
    users.filter(u => !userSearch || u.name.includes(userSearch)), [users, userSearch])

  const toggleUser = uid => setForm(f => ({
    ...f, assigned_to: f.assigned_to.includes(uid)
      ? f.assigned_to.filter(id => id !== uid) : [...f.assigned_to, uid]
  }))
  const toggleDept = dept => {
    const ids = filteredUsers.filter(u => (u.department || '未分部門') === dept).map(u => u.id)
    const allSel = ids.every(id => form.assigned_to.includes(id))
    setForm(f => ({ ...f, assigned_to: allSel ? f.assigned_to.filter(id => !ids.includes(id)) : [...new Set([...f.assigned_to, ...ids])] }))
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 }}>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={st.label}>{_('taskNameLabel')}</label>
          <input style={st.input} value={form.title} placeholder={_('taskNamePh')} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={st.label}>{_('taskDescLabel')}</label>
          <textarea style={{ ...st.input, minHeight: 68, resize: 'vertical' }} value={form.description}
            placeholder="詳細說明任務內容、步驟、注意事項..." onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
        <div>
          <label style={st.label}>{_('taskCategoryLabel')}</label>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {categories.map(c => (
              <button key={c.key} type="button" onClick={() => setForm(f => ({ ...f, category: c.key }))}
                style={{ padding: '7px 11px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `2px solid ${form.category === c.key ? c.color : C.border}`, background: form.category === c.key ? c.color + '18' : '#fff', color: form.category === c.key ? c.color : C.muted }}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={st.label}>{_('taskPriorityLabel')}</label>
          <div style={{ display: 'flex', gap: 5 }}>
            {priorities.map(p => (
              <button key={p.key} type="button" onClick={() => setForm(f => ({ ...f, priority: p.key }))}
                style={{ flex: 1, padding: '10px 6px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `2px solid ${form.priority === p.key ? p.color : C.border}`, background: form.priority === p.key ? p.bg : '#fff', color: form.priority === p.key ? p.color : C.muted }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={st.label}>{_('taskDueDateLabel')}</label>
          <input type="date" style={st.input} value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
        </div>
      </div>

      {/* User assignment */}
      <label style={st.label}>
        {_('taskAssignLabel')}
        <span style={{ color: C.muted, fontWeight: 400, marginLeft: 6 }}>（已選 {form.assigned_to.length} 人）</span>
      </label>

      {form.assigned_to.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10, padding: '8px 12px', background: C.accentDim, borderRadius: 10, border: `1.5px solid ${C.accent}44` }}>
          {form.assigned_to.map(uid => {
            const u = users.find(u => u.id === uid)
            return u ? (
              <span key={uid} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', background: C.accent, color: '#fff', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                {u.name}
                <button type="button" onClick={() => toggleUser(uid)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }}>✕</button>
              </span>
            ) : null
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input style={{ ...st.input, flex: 1, minWidth: 120, fontSize: 12 }} placeholder={_('taskSearchEmployee')} value={userSearch} onChange={e => setUserSearch(e.target.value)} />
        <button type="button" style={{ ...st.btn('primary'), padding: '7px 12px', fontSize: 12 }}
          onClick={() => setForm(f => ({ ...f, assigned_to: filteredUsers.map(u => u.id) }))}>全選</button>
        <button type="button" style={{ ...st.btn('default'), padding: '7px 12px', fontSize: 12 }}
          onClick={() => setForm(f => ({ ...f, assigned_to: [] }))}>清除</button>
      </div>

      <div style={{ maxHeight: 280, overflowY: 'auto', border: `1.5px solid ${C.border}`, borderRadius: 10, padding: 8 }}>
        {depts.map(dept => {
          const deptUsers = filteredUsers.filter(u => (u.department || '未分部門') === dept)
          if (!deptUsers.length) return null
          return (
            <div key={dept} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, padding: '0 4px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.5 }}>{dept}</span>
                <button type="button" onClick={() => toggleDept(dept)}
                  style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, cursor: 'pointer', background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }}>
                  {deptUsers.every(u => form.assigned_to.includes(u.id)) ? '取消全選' : '全選'}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 5 }}>
                {deptUsers.map(u => {
                  const sel = form.assigned_to.includes(u.id)
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: sel ? C.accentDim : '#f8fafc', border: `1.5px solid ${sel ? C.accent : C.border}` }}>
                      <input type="checkbox" checked={sel} onChange={() => toggleUser(u.id)} style={{ width: 15, height: 15, flexShrink: 0, accentColor: C.accent }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: sel ? 700 : 400, color: sel ? C.accent : C.text }}>{u.name}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>{u.position || ''}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Employee task card ────────────────────────────────────────────────────────
function MyTaskCard({ task, prog, saving, onStart, onComplete, onHelp, onResume, catInfo, priInfo, progInfo, _ }) {
  const [showNote, setShowNote] = useState(false)
  const [completionNote, setCompletionNote] = useState('')
  const status = prog?.status || 'pending'
  const pi     = progInfo(status)
  const cat    = catInfo(task.category)
  const pri    = priInfo(task.priority)
  const isDone = status === 'completed'
  const days   = daysUntil(task.due_date)
  const isOverdue = !isDone && days !== null && days < 0

  const borderColor = isOverdue ? '#ef4444'
    : status === 'help_requested' ? '#ef4444'
    : status === 'completed'      ? '#22c55e'
    : status === 'in_progress'    ? '#f59e0b'
    : C.border

  const bgColor = isOverdue       ? '#fff5f5'
    : status === 'help_requested' ? '#fff5f5'
    : status === 'completed'      ? '#f0fdf4'
    : '#fff'

  return (
    <div style={{ background: bgColor, borderRadius: 14, border: `2px solid ${borderColor}`, padding: '14px 16px', marginBottom: 12, opacity: isDone ? 0.85 : 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
            <span style={{ fontSize: 18 }}>{cat.icon}</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: isDone ? '#22c55e' : C.text, textDecoration: isDone ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.title}</span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: pri.bg, color: pri.color, border: `1px solid ${pri.color}44`, flexShrink: 0 }}>
              {pri.label}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: C.muted }}>{cat.label}</span>
            <DueDateBadge dateStr={task.due_date} isDone={isDone} />
            <span style={{ fontSize: 11, color: C.muted }}>指派：{task.assigned_by}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <div style={{ fontSize: 22 }}>{pi.icon}</div>
          <span style={{ fontSize: 10, fontWeight: 700, color: pi.color, whiteSpace: 'nowrap' }}>{pi.label}</span>
        </div>
      </div>

      {task.description && (
        <div style={{ fontSize: 12, color: C.sub, background: '#f8fafc', borderRadius: 8, padding: '8px 12px', marginBottom: 10, lineHeight: 1.6 }}>
          {task.description}
        </div>
      )}

      {prog?.help_note && status === 'help_requested' && (
        <div style={{ fontSize: 12, color: '#ef4444', background: '#fee2e2', border: '1.5px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
          🆘 協助需求：{prog.help_note}
        </div>
      )}

      {isDone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#dcfce7', borderRadius: 10, marginTop: 4 }}>
          <span style={{ fontSize: 18 }}>🎉</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>任務完成！</div>
            {prog?.completed_at && <div style={{ fontSize: 11, color: C.muted }}>完成時間 {formatDate(prog.completed_at)}</div>}
            {prog?.help_note && <div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>📝 {prog.help_note}</div>}
          </div>
        </div>
      )}

      {!isDone && (
        <div>
          {/* Completion note input */}
          {showNote && (
            <div style={{ marginBottom: 10 }}>
              <textarea style={{ ...st.input, fontSize: 12, minHeight: 60, resize: 'vertical' }} value={completionNote}
                placeholder="完成備注（選填）— 說明完成情況、心得或問題..."
                onChange={e => setCompletionNote(e.target.value)} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {status === 'pending' && (
              <button style={{ ...st.btn('primary'), flex: 1, padding: '11px', minHeight: 48, fontSize: 13 }}
                onClick={() => onStart(task)} disabled={saving}>{_('taskBtnStart')}</button>
            )}
            {status === 'in_progress' && (
              <>
                <button style={{ ...st.btn('success'), flex: 1, padding: '11px', minHeight: 48, fontSize: 13 }}
                  onClick={() => { if (!showNote) { setShowNote(true) } else { onComplete(task, completionNote); setShowNote(false); setCompletionNote('') } }}
                  disabled={saving}>
                  {showNote ? _('taskBtnConfirmComplete') : _('taskBtnComplete')}
                </button>
                {showNote && (
                  <button style={{ ...st.btn('default'), padding: '11px 14px', minHeight: 48, fontSize: 12 }}
                    onClick={() => { setShowNote(false); setCompletionNote('') }}>{_('cancel')}</button>
                )}
              </>
            )}
            {(status === 'in_progress' || status === 'pending') && (
              <button style={{ ...st.btn('danger'), padding: '11px 14px', minHeight: 48, fontSize: 12 }}
                onClick={() => onHelp(task, prog?.help_note || '')} disabled={saving}>
                {_('taskBtnAskHelp')}
              </button>
            )}
            {status === 'help_requested' && (
              <button style={{ ...st.btn('primary'), flex: 1, padding: '11px', minHeight: 48, fontSize: 13 }}
                onClick={() => onResume(task)} disabled={saving}>{_('taskBtnResume')}</button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Tasks({ userProfile, showToast, addLog, isMobile = false, enabledModules = [] }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)

  const CATEGORIES = [
    { key: 'general',     label: _('taskCatGeneral'),     icon: '📋', color: C.accent },
    { key: 'safety',      label: _('taskCatSafety'),      icon: '🦺', color: '#f97316' },
    { key: 'quality',     label: _('taskCatQuality'),     icon: '✅', color: '#22c55e' },
    { key: 'training',    label: _('taskCatTraining'),    icon: '📚', color: '#7c3aed' },
    { key: 'maintenance', label: _('taskCatMaintenance'), icon: '🔧', color: '#0891b2' },
    { key: 'improvement', label: _('taskCatImprovement'), icon: '💡', color: '#d97706' },
  ]

  const PRIORITIES = [
    { key: 'urgent', label: _('taskPriUrgent'), color: '#ef4444', bg: '#fee2e2' },
    { key: 'high',   label: _('taskPriHigh'),   color: '#f97316', bg: '#fff7ed' },
    { key: 'normal', label: _('taskPriNormal'), color: C.accent,  bg: C.accentDim },
    { key: 'low',    label: _('taskPriLow'),    color: C.muted,   bg: '#f1f5f9' },
  ]

  const PROGRESS_STATUS = [
    { key: 'pending',        label: _('taskStatusPending'),    icon: '⏳', color: '#94a3b8' },
    { key: 'in_progress',    label: _('taskStatusInProgress'), icon: '⚡', color: '#f59e0b' },
    { key: 'help_requested', label: _('taskStatusHelp'),       icon: '🆘', color: '#ef4444' },
    { key: 'completed',      label: _('taskStatusCompleted'),  icon: '✅', color: '#22c55e' },
  ]

  const catInfo  = k => CATEGORIES.find(c => c.key === k)      || CATEGORIES[0]
  const priInfo  = k => PRIORITIES.find(p => p.key === k)      || PRIORITIES[2]
  const progInfo = k => PROGRESS_STATUS.find(p => p.key === k) || PROGRESS_STATUS[0]

  const isAdmin   = userProfile?.role === 'admin'
  const canAssign = isAdmin || enabledModules.includes('assign') || ['factory_manager', 'senior_manager', 'section_chief', 'team_leader'].includes(userProfile?.position)

  const [activeTab, setActiveTab] = useState('my')
  const [tasks,    setTasks]    = useState([])
  const [archived, setArchived] = useState([])
  const [progress, setProgress] = useState([])
  const [users,    setUsers]    = useState([])
  const [saving,   setSaving]   = useState(false)

  // My tasks
  const [showDone, setShowDone] = useState(false)

  // Help modal
  const [helpModal, setHelpModal] = useState(null)
  const [helpNote,  setHelpNote]  = useState('')

  // Admin: filters
  const [searchQ,    setSearchQ]    = useState('')
  const [filterCat,  setFilterCat]  = useState('all')
  const [filterPri,  setFilterPri]  = useState('all')
  const [filterStatus, setFilterStatus] = useState('active') // 'active' | 'archived'
  const [sortBy,     setSortBy]     = useState('created')    // 'created' | 'due' | 'priority' | 'progress'
  const [selectedTask, setSelectedTask] = useState(null)

  // Admin: create / edit
  const [showForm, setShowForm] = useState(false)
  const [editTask, setEditTask] = useState(null)  // null = create, obj = edit
  const [form, setForm] = useState({ title: '', description: '', category: 'general', priority: 'normal', assigned_to: [], due_date: '' })

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    const cid = userProfile?.company_id
    const taskQuery = (status) => {
      let q = supabase.from('tasks').select('*').eq('status', status).order('created_at', { ascending: false }).limit(500)
      if (cid) q = q.eq('company_id', cid)
      return q
    }
    const progQuery = () => {
      let q = supabase.from('task_progress').select('*').limit(2000)
      return q
    }
    const userQuery = () => {
      let q = supabase.from('user_profiles').select('id, name, position, department').eq('approved', true).order('name').limit(200)
      if (cid) q = q.eq('company_id', cid)
      return q
    }
    const [{ data: t }, { data: arch }, { data: p }, { data: u }] = await Promise.all([
      taskQuery('active'), taskQuery('archived'), progQuery(), userQuery(),
    ])
    if (t)    setTasks(t)
    if (arch) setArchived(arch)
    if (p)    setProgress(p)
    if (u)    setUsers(u)
  }, [userProfile?.company_id])

  useEffect(() => { load() }, [userProfile?.id])

  // ── Derived ───────────────────────────────────────────────────────────────
  const myTasks = useMemo(() => tasks.filter(t => t.assigned_to?.includes(userProfile?.id)), [tasks, userProfile?.id])

  const myProgressMap = useMemo(() => {
    const map = {}
    progress.forEach(p => { if (p.user_id === userProfile?.id) map[p.task_id] = p })
    return map
  }, [progress, userProfile?.id])

  const myStats = useMemo(() => ({
    total:      myTasks.length,
    completed:  myTasks.filter(t => myProgressMap[t.id]?.status === 'completed').length,
    inProgress: myTasks.filter(t => myProgressMap[t.id]?.status === 'in_progress').length,
    pending:    myTasks.filter(t => !myProgressMap[t.id] || myProgressMap[t.id]?.status === 'pending').length,
    overdue:    myTasks.filter(t => myProgressMap[t.id]?.status !== 'completed' && daysUntil(t.due_date) !== null && daysUntil(t.due_date) < 0).length,
    help:       myTasks.filter(t => myProgressMap[t.id]?.status === 'help_requested').length,
  }), [myTasks, myProgressMap])

  // Group my tasks for display
  const groupedMyTasks = useMemo(() => {
    const active = myTasks.filter(t => {
      const st = myProgressMap[t.id]?.status || 'pending'
      return showDone ? true : st !== 'completed'
    })
    const overdue    = active.filter(t => { const d = daysUntil(t.due_date); return d !== null && d < 0 && myProgressMap[t.id]?.status !== 'completed' })
    const urgent     = active.filter(t => { const d = daysUntil(t.due_date); const s = myProgressMap[t.id]?.status; return !overdue.includes(t) && s !== 'completed' && (t.priority === 'urgent' || (d !== null && d <= 3)) })
    const inProgress = active.filter(t => !overdue.includes(t) && !urgent.includes(t) && myProgressMap[t.id]?.status === 'in_progress')
    const normal     = active.filter(t => !overdue.includes(t) && !urgent.includes(t) && !inProgress.includes(t) && myProgressMap[t.id]?.status !== 'completed')
    const done       = showDone ? active.filter(t => myProgressMap[t.id]?.status === 'completed') : []
    return { overdue, urgent, inProgress, normal, done }
  }, [myTasks, myProgressMap, showDone])

  // Admin: filtered + sorted tasks
  const displayTasks = useMemo(() => {
    const source = filterStatus === 'archived' ? archived : tasks
    let list = source.filter(t => {
      const matchSearch = !searchQ || t.title.toLowerCase().includes(searchQ.toLowerCase()) || (t.description || '').toLowerCase().includes(searchQ.toLowerCase())
      const matchCat    = filterCat === 'all' || t.category === filterCat
      const matchPri    = filterPri === 'all' || t.priority === filterPri
      return matchSearch && matchCat && matchPri
    })
    if (sortBy === 'due') {
      list = [...list].sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0
        if (!a.due_date) return 1
        if (!b.due_date) return -1
        return a.due_date.localeCompare(b.due_date)
      })
    } else if (sortBy === 'priority') {
      const order = { urgent: 0, high: 1, normal: 2, low: 3 }
      list = [...list].sort((a, b) => (order[a.priority] ?? 2) - (order[b.priority] ?? 2))
    } else if (sortBy === 'progress') {
      list = [...list].sort((a, b) => {
        const progA = a.assigned_to?.length > 0 ? progress.filter(p => p.task_id === a.id && p.status === 'completed').length / a.assigned_to.length : 0
        const progB = b.assigned_to?.length > 0 ? progress.filter(p => p.task_id === b.id && p.status === 'completed').length / b.assigned_to.length : 0
        return progA - progB
      })
    }
    return list
  }, [tasks, archived, filterStatus, searchQ, filterCat, filterPri, sortBy, progress])

  // Admin stats
  const adminStats = useMemo(() => {
    const helpReqs = progress.filter(p => p.status === 'help_requested')
    const overdueTasks = tasks.filter(t => daysUntil(t.due_date) !== null && daysUntil(t.due_date) < 0)
    const completedAll = progress.filter(p => p.status === 'completed').length
    const totalAssigned = tasks.reduce((a, t) => a + (t.assigned_to?.length || 0), 0)
    const completionRate = totalAssigned > 0 ? Math.round(completedAll / totalAssigned * 100) : 0
    return { helpReqs, overdueTasks, completedAll, totalAssigned, completionRate }
  }, [tasks, progress])

  // Analytics
  const analytics = useMemo(() => {
    // User completion rates
    const userMap = {}
    progress.forEach(p => {
      if (!userMap[p.user_name]) userMap[p.user_name] = { name: p.user_name, total: 0, completed: 0, help: 0 }
      userMap[p.user_name].total++
      if (p.status === 'completed') userMap[p.user_name].completed++
      if (p.status === 'help_requested') userMap[p.user_name].help++
    })
    const userRanking = Object.values(userMap)
      .map(u => ({ ...u, rate: u.total > 0 ? Math.round(u.completed / u.total * 100) : 0 }))
      .sort((a, b) => b.rate - a.rate)

    // By category
    const byCat = CATEGORIES.map(c => ({
      label: c.label, icon: c.icon, color: c.color,
      count: tasks.filter(t => t.category === c.key).length,
    })).filter(c => c.count > 0)

    // By priority
    const byPri = PRIORITIES.map(p => ({
      label: p.label, color: p.color, bg: p.bg,
      count: tasks.filter(t => t.priority === p.key).length,
    })).filter(p => p.count > 0)

    // Overdue tasks list
    const overdueTasks = tasks
      .filter(t => daysUntil(t.due_date) !== null && daysUntil(t.due_date) < 0)
      .sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))

    return { userRanking, byCat, byPri, overdueTasks }
  }, [tasks, progress, CATEGORIES, PRIORITIES])

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleUpdateStatus = async (task, newStatus, note = '') => {
    const existing = myProgressMap[task.id]
    setSaving(true)
    const payload = {
      task_id: task.id, user_id: userProfile.id, user_name: userProfile.name,
      status: newStatus, updated_at: new Date().toISOString(),
      completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
      help_requested: newStatus === 'help_requested',
      help_note: note || null,
    }
    if (existing) {
      await supabase.from('task_progress').update(payload).eq('id', existing.id)
    } else {
      await supabase.from('task_progress').insert(payload)
    }
    await addLog('更新任務', `${task.title} → ${progInfo(newStatus).label}${note ? `：${note}` : ''}`)
    showToast(`${progInfo(newStatus).icon} ${task.title} — ${progInfo(newStatus).label}`)
    setSaving(false)
    load()
  }

  const handleRequestHelp = async () => {
    if (!helpNote.trim()) { showToast('請說明需要什麼協助', 'error'); return }
    await handleUpdateStatus(helpModal, 'help_requested', helpNote)
    setHelpModal(null); setHelpNote('')
  }

  const handleSaveTask = async () => {
    if (!form.title.trim()) { showToast('請填寫任務名稱', 'error'); return }
    if (form.assigned_to.length === 0) { showToast('請選擇指派人員', 'error'); return }
    setSaving(true)
    if (editTask) {
      // Edit existing
      const { due_date, ...rest } = form
      await supabase.from('tasks').update({ ...rest, due_date: due_date || null }).eq('id', editTask.id)
      await addLog('編輯任務', form.title)
      showToast(`✅ 任務「${form.title}」已更新`)
    } else {
      // Create new
      const { due_date, ...rest } = form
      await supabase.from('tasks').insert({ ...rest, due_date: due_date || null, assigned_by: userProfile?.name, status: 'active', company_id: userProfile?.company_id || null })
      await addLog('建立任務', `${form.title}，指派 ${form.assigned_to.length} 人`)
      showToast(`✅ 任務「${form.title}」已建立，指派給 ${form.assigned_to.length} 人`)
      // 寫入 in-app 通知
      if (form.assigned_to.length > 0) {
        await supabase.from('notifications').insert(
          form.assigned_to.map(uid => ({
            user_id: uid,
            title: '📋 新任務指派',
            body: `${userProfile?.name} 指派了任務：${form.title}`,
            url: '/',
          }))
        )
      }
      // 推播通知給被指派的員工（不等待，避免阻塞 UI）
      const { data: { session: notifSession } } = await supabase.auth.getSession()
      fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${notifSession?.access_token}` },
        body: JSON.stringify({
          user_ids: form.assigned_to,
          title: '📋 新任務指派',
          body: `${userProfile?.name} 指派了任務：${form.title}`,
          url: '/',
        }),
      }).catch(() => {})
    }
    setShowForm(false); setEditTask(null)
    setForm({ title: '', description: '', category: 'general', priority: 'normal', assigned_to: [], due_date: '' })
    setSaving(false); load()
  }

  const handleArchive = async (id, title) => {
    if (!window.confirm(`封存任務「${title}」？（可在歷史記錄中查看）`)) return
    await supabase.from('tasks').update({ status: 'archived' }).eq('id', id)
    await addLog('封存任務', title)
    showToast(`📦 任務「${title}」已封存`)
    load()
  }

  const handleRestore = async (id, title) => {
    await supabase.from('tasks').update({ status: 'active' }).eq('id', id)
    await addLog('還原任務', title)
    showToast(`✅ 任務「${title}」已還原為進行中`)
    load()
  }

  const handleDelete = async (id, title) => {
    if (!window.confirm(`確定要永久刪除任務「${title}」？\n\n此操作無法復原，所有員工的進度記錄將一併刪除。`)) return
    await supabase.from('task_progress').delete().eq('task_id', id)
    await supabase.from('tasks').delete().eq('id', id)
    await addLog('刪除任務', title)
    showToast(`🗑 任務「${title}」已永久刪除`)
    if (selectedTask?.id === id) setSelectedTask(null)
    load()
  }

  const openEdit = task => {
    setEditTask(task)
    setForm({ title: task.title, description: task.description || '', category: task.category, priority: task.priority, assigned_to: task.assigned_to || [], due_date: task.due_date || '' })
    setShowForm(true)
  }

  // 再次提醒：只發給「尚未完成」的員工
  const [remindingId, setRemindingId] = useState(null)
  const handleRemind = async (task) => {
    const taskProgs = progress.filter(p => p.task_id === task.id)
    const doneIds = new Set(taskProgs.filter(p => p.status === 'completed').map(p => p.user_id))
    const pendingIds = (task.assigned_to || []).filter(uid => !doneIds.has(uid))
    if (pendingIds.length === 0) { showToast('所有員工已完成此任務！', 'error'); return }

    setRemindingId(task.id)
    try {
      // in-app 通知
      await supabase.from('notifications').insert(
        pendingIds.map(uid => ({
          user_id: uid,
          title: '📋 任務提醒',
          body: `${userProfile?.name} 提醒您完成任務：${task.title}`,
          url: '/',
        }))
      )
      // 推播
      const { data: { session: s } } = await supabase.auth.getSession()
      await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s?.access_token}` },
        body: JSON.stringify({
          user_ids: pendingIds,
          title: '📋 任務提醒',
          body: `${userProfile?.name} 提醒您完成任務：${task.title}`,
          url: '/',
        }),
      }).catch(() => {})
      await addLog('任務提醒', `提醒 ${pendingIds.length} 人完成：${task.title}`)
      showToast(`🔔 已提醒 ${pendingIds.length} 位尚未完成的員工`)
    } catch (err) {
      showToast('提醒失敗：' + err.message, 'error')
    }
    setRemindingId(null)
  }

  // ── Render helpers ─────────────────────────────────────────────────────────
  const TaskGroup = ({ label, color, tasks: list }) => {
    if (!list.length) return null
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
          {label} <span style={{ fontSize: 11, fontWeight: 500 }}>({list.length})</span>
        </div>
        {list.map(task => (
          <MyTaskCard key={task.id} task={task} prog={myProgressMap[task.id]} saving={saving}
            catInfo={catInfo} priInfo={priInfo} progInfo={progInfo} _={_}
            onStart={t => handleUpdateStatus(t, 'in_progress')}
            onComplete={(t, note) => handleUpdateStatus(t, 'completed', note)}
            onHelp={(t, note) => { setHelpModal(t); setHelpNote(note) }}
            onResume={t => handleUpdateStatus(t, 'in_progress')}
          />
        ))}
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  const TABS = [
    { key: 'my',        icon: '📝', label: _('taskTabMy') },
    ...(canAssign ? [
      { key: 'manage',    icon: '⚙️',  label: _('taskTabManage') },
      { key: 'analytics', icon: '📊',  label: _('taskTabAnalytics') },
    ] : []),
  ]

  return (
    <div>
      <PageHeader title="任務管理" en="Task Management" vi="Nhiệm vụ" />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key}
            style={{ ...st.btn(activeTab === t.key ? 'primary' : 'default'), padding: '10px 18px', fontSize: 13, position: 'relative' }}
            onClick={() => setActiveTab(t.key)}>
            {t.icon} {t.label}
            {t.key === 'my' && myStats.overdue > 0 && (
              <span style={{ position: 'absolute', top: -5, right: -5, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{myStats.overdue}</span>
            )}
            {t.key === 'manage' && adminStats.helpReqs.length > 0 && (
              <span style={{ position: 'absolute', top: -5, right: -5, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{adminStats.helpReqs.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ══════════════ TAB: 我的任務 ══════════════ */}
      {activeTab === 'my' && (
        <>
          {myTasks.length === 0 ? (
            <div style={{ ...st.card, textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{_('taskNoTasks')}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>{_('taskRelax')}</div>
            </div>
          ) : (
            <>
              {/* Progress banner */}
              <div style={{ marginBottom: 16, background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)', borderRadius: 16, padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)', marginBottom: 4 }}>我的任務進度</div>
                    <div style={{ fontSize: 26, fontWeight: 900, color: '#fff' }}>
                      {myStats.completed}<span style={{ fontSize: 14, opacity: 0.7 }}> / {myStats.total} 已完成</span>
                    </div>
                    <div style={{ marginTop: 8, background: 'rgba(255,255,255,0.2)', borderRadius: 6, height: 8, width: 180, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.round(myStats.completed / Math.max(myStats.total, 1) * 100)}%`, height: '100%', background: '#fff', borderRadius: 6 }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {[
                      ['進行中', myStats.inProgress, '#fde68a'],
                      ['未開始', myStats.pending, 'rgba(255,255,255,0.65)'],
                      ...(myStats.overdue > 0 ? [['已逾期', myStats.overdue, '#fca5a5']] : []),
                      ...(myStats.help > 0    ? [['需協助', myStats.help, '#fca5a5']]   : []),
                    ].map(([label, val, color]) => (
                      <div key={label} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, fontWeight: 900, color }}>{val}</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Show/hide done toggle */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button style={{ ...st.btn('default'), padding: '7px 14px', fontSize: 12 }} onClick={() => setShowDone(v => !v)}>
                  {showDone ? '🙈 隱藏已完成' : `👁 顯示已完成（${myStats.completed}）`}
                </button>
              </div>

              {/* Grouped tasks */}
              <TaskGroup label={_('taskGroupOverdue')}     color="#ef4444" tasks={groupedMyTasks.overdue} />
              <TaskGroup label={_('taskGroupUrgent')}      color="#f97316" tasks={groupedMyTasks.urgent} />
              <TaskGroup label={_('taskGroupInProgress')}  color="#f59e0b" tasks={groupedMyTasks.inProgress} />
              <TaskGroup label={_('taskGroupNotStarted')}  color="#94a3b8" tasks={groupedMyTasks.normal} />
              <TaskGroup label={_('taskGroupDone')}        color="#22c55e" tasks={groupedMyTasks.done} />

              {!showDone && myStats.completed > 0 && groupedMyTasks.overdue.length === 0 && groupedMyTasks.urgent.length === 0 && groupedMyTasks.inProgress.length === 0 && groupedMyTasks.normal.length === 0 && (
                <div style={{ ...st.card, textAlign: 'center', padding: '32px' }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#22c55e' }}>{_('taskAllDone')}</div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ══════════════ TAB: 指派管理 ══════════════ */}
      {activeTab === 'manage' && canAssign && (
        <>
          {/* Help requests alert */}
          {adminStats.helpReqs.length > 0 && (
            <div style={{ ...st.card, marginBottom: 16, background: '#fff5f5', border: '2px solid #fca5a5' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#ef4444', marginBottom: 8 }}>🆘 {adminStats.helpReqs.length} 人需要協助</div>
              {adminStats.helpReqs.map(h => {
                const task = tasks.find(t => t.id === h.task_id)
                return (
                  <div key={h.id} style={{ padding: '9px 12px', background: '#fff', borderRadius: 10, marginBottom: 6, border: '1.5px solid #fca5a5' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{h.user_name}</span>
                    <span style={{ fontSize: 13, color: C.muted }}> 需要協助：</span>
                    <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{task?.title}</span>
                    {h.help_note && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>「{h.help_note}」</div>}
                  </div>
                )
              })}
            </div>
          )}

          {/* Overdue alert */}
          {adminStats.overdueTasks.length > 0 && (
            <div style={{ ...st.card, marginBottom: 16, background: '#fff7ed', border: '2px solid #fed7aa' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#f97316', marginBottom: 6 }}>⏰ {adminStats.overdueTasks.length} 個任務已逾期</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {adminStats.overdueTasks.slice(0, 5).map(t => (
                  <span key={t.id} style={{ fontSize: 11, padding: '3px 10px', background: '#fee2e2', color: '#ef4444', borderRadius: 20, fontWeight: 600 }}>
                    {t.title} ({Math.abs(daysUntil(t.due_date))}天前)
                  </span>
                ))}
                {adminStats.overdueTasks.length > 5 && <span style={{ fontSize: 11, color: C.muted }}>+{adminStats.overdueTasks.length - 5} 個...</span>}
              </div>
            </div>
          )}

          {/* KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
            {[
              { icon: '📋', label: '進行中任務', value: tasks.length, color: C.accent, bg: C.accentDim },
              { icon: '✅', label: '整體完成率', value: `${adminStats.completionRate}%`, color: '#22c55e', bg: '#dcfce7' },
              { icon: '⚡', label: '進行中', value: progress.filter(p => p.status === 'in_progress').length, color: '#f59e0b', bg: '#fef9c3' },
              { icon: '🆘', label: '待協助', value: adminStats.helpReqs.length, color: adminStats.helpReqs.length > 0 ? '#ef4444' : C.muted, bg: adminStats.helpReqs.length > 0 ? '#fee2e2' : '#f1f5f9' },
              { icon: '📦', label: '已封存', value: archived.length, color: C.muted, bg: '#f1f5f9' },
            ].map(k => (
              <div key={k.label} style={{ background: k.bg, border: `1.5px solid ${k.color}33`, borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ fontSize: 18 }}>{k.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: k.color, lineHeight: 1.1, marginTop: 4 }}>{k.value}</div>
                <div style={{ fontSize: 11, color: k.color, fontWeight: 600, marginTop: 3 }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <input style={{ ...st.input, flex: 1, minWidth: 140, fontSize: 13 }} placeholder="🔍 搜尋任務名稱" value={searchQ} onChange={e => setSearchQ(e.target.value)} />
            <select style={{ ...st.input, width: 110 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
              <option value="all">全部分類</option>
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
            </select>
            <select style={{ ...st.input, width: 100 }} value={filterPri} onChange={e => setFilterPri(e.target.value)}>
              <option value="all">全部優先</option>
              {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <select style={{ ...st.input, width: 110 }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="created">最新建立</option>
              <option value="due">截止日期</option>
              <option value="priority">優先程度</option>
              <option value="progress">完成率↑</option>
            </select>
          </div>

          {/* Active / Archived toggle + actions */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {[{ key: 'active', label: `進行中（${tasks.length}）` }, { key: 'archived', label: `已封存（${archived.length}）` }].map(s => (
                <button key={s.key} style={{ ...st.btn(filterStatus === s.key ? 'primary' : 'default'), padding: '8px 14px', fontSize: 12 }} onClick={() => setFilterStatus(s.key)}>
                  {s.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {filterStatus === 'active' && (
                <button style={{ ...st.btn(), padding: '9px 16px', fontSize: 13 }} onClick={() => { setEditTask(null); setForm({ title: '', description: '', category: 'general', priority: 'normal', assigned_to: [], due_date: '' }); setShowForm(!showForm) }}>
                  {showForm ? `✕ ${_('cancel')}` : _('taskNewTask')}
                </button>
              )}
              <button style={{ ...st.btn('default'), padding: '9px 14px', fontSize: 12 }}
                onClick={() => exportCSV(
                  `任務清單_${new Date().toLocaleDateString('zh-TW').replace(/\//g,'-')}.csv`,
                  ['任務名稱', '分類', '優先', '截止日期', '指派人', '指派數', '完成數', '完成率', '狀態'],
                  displayTasks.map(t => {
                    const tp = progress.filter(p => p.task_id === t.id)
                    const done = tp.filter(p => p.status === 'completed').length
                    const total = t.assigned_to?.length || 0
                    return [t.title, catInfo(t.category).label, priInfo(t.priority).label, t.due_date || '—', t.assigned_by, total, done, total > 0 ? `${Math.round(done/total*100)}%` : '—', filterStatus]
                  })
                )}>
                📥 匯出
              </button>
            </div>
          </div>

          {/* Create / edit form */}
          {showForm && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 14 }}>
                {editTask ? _('taskFormEdit') : _('taskFormNew')}
              </div>
              <TaskFormBody form={form} setForm={setForm} users={users} categories={CATEGORIES} priorities={PRIORITIES} _={_} />
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '13px' }} onClick={handleSaveTask} disabled={saving}>
                  {saving ? _('savingBtn') : editTask ? `確認更新` : `確認建立（指派 ${form.assigned_to.length} 人）`}
                </button>
                <button style={{ ...st.btn('default'), padding: '13px 18px' }}
                  onClick={() => { setShowForm(false); setEditTask(null) }}>{_('cancel')}</button>
              </div>
            </div>
          )}

          {/* Task list */}
          {displayTasks.length === 0 ? (
            <div style={st.card}><EmptyState icon="📋" text={filterStatus === 'archived' ? _('taskEmptyArchived') : _('taskEmptyNoTask')} /></div>
          ) : displayTasks.map(task => {
            const taskProgs = progress.filter(p => p.task_id === task.id)
            const assignedCount = task.assigned_to?.length || 0
            const doneCount     = taskProgs.filter(p => p.status === 'completed').length
            const helpCount     = taskProgs.filter(p => p.status === 'help_requested').length
            const pct           = assignedCount > 0 ? Math.round(doneCount / assignedCount * 100) : 0
            const cat  = catInfo(task.category)
            const pri  = priInfo(task.priority)
            const isExpanded = selectedTask?.id === task.id
            const isOverdue = filterStatus === 'active' && daysUntil(task.due_date) !== null && daysUntil(task.due_date) < 0

            return (
              <div key={task.id} style={{ ...st.card, marginBottom: 10, border: `2px solid ${helpCount > 0 ? '#fca5a5' : isOverdue ? '#fed7aa' : C.border}`, background: isOverdue ? '#fffbf0' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 16 }}>{cat.icon}</span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{task.title}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: pri.bg, color: pri.color }}>
                        {pri.label}
                      </span>
                      {helpCount > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#fee2e2', color: '#ef4444' }}>
                          🆘 {helpCount} 人求助
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: C.muted }}>{cat.label}</span>
                      <DueDateBadge dateStr={task.due_date} isDone={pct === 100} />
                      <span style={{ fontSize: 11, color: C.muted }}>建立：{task.assigned_by}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexShrink: 0, flexWrap: 'wrap' }}>
                    <button style={{ ...st.btn('default'), padding: '5px 10px', fontSize: 11 }}
                      onClick={() => setSelectedTask(isExpanded ? null : task)}>
                      {isExpanded ? '▲' : '▼'} {_('taskBtnDetail')}
                    </button>
                    {filterStatus === 'active' && pct < 100 && (
                      <button
                        style={{ padding: '5px 10px', fontSize: 11, borderRadius: 8, border: '1.5px solid #f59e0b', background: remindingId === task.id ? '#fef9c3' : '#fffbeb', color: '#b45309', fontWeight: 700, cursor: remindingId === task.id ? 'not-allowed' : 'pointer', opacity: remindingId === task.id ? 0.7 : 1 }}
                        disabled={remindingId === task.id}
                        onClick={() => handleRemind(task)}>
                        {remindingId === task.id ? '提醒中...' : _('taskBtnRemind')}
                      </button>
                    )}
                    {filterStatus === 'active' && (
                      <button style={{ ...st.btn('default'), padding: '5px 10px', fontSize: 11 }}
                        onClick={() => openEdit(task)}>{_('taskBtnEdit')}</button>
                    )}
                    {filterStatus === 'active' ? (
                      <button style={{ ...st.btn('warning'), padding: '5px 10px', fontSize: 11 }}
                        onClick={() => handleArchive(task.id, task.title)}>{_('taskBtnArchive')}</button>
                    ) : (
                      <button style={{ ...st.btn('success'), padding: '5px 10px', fontSize: 11 }}
                        onClick={() => handleRestore(task.id, task.title)}>{_('taskBtnRestore')}</button>
                    )}
                    <button style={{ ...st.btn('danger'), padding: '5px 10px', fontSize: 11 }}
                      onClick={() => handleDelete(task.id, task.title)}>{_('taskBtnDelete')}</button>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom: isExpanded ? 12 : 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
                    <span style={{ color: C.muted }}>完成進度</span>
                    <span style={{ fontWeight: 700, color: pct === 100 ? '#22c55e' : C.accent }}>{doneCount}/{assignedCount} 人 ({pct}%)</span>
                  </div>
                  <div style={{ background: '#f1f5f9', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#22c55e' : C.accent, borderRadius: 6, transition: 'width 0.4s' }} />
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ marginTop: 12 }}>
                    {task.description && (
                      <div style={{ fontSize: 12, color: C.sub, background: '#f8fafc', borderRadius: 10, padding: '10px 14px', marginBottom: 12, lineHeight: 1.6 }}>{task.description}</div>
                    )}
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>員工完成狀態</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))', gap: 7 }}>
                      {task.assigned_to?.map(uid => {
                        const u  = users.find(u => u.id === uid)
                        const p  = taskProgs.find(p => p.user_id === uid)
                        const pi = progInfo(p?.status || 'pending')
                        return (
                          <div key={uid} style={{ padding: '10px 12px', background: p?.status === 'completed' ? '#f0fdf4' : p?.status === 'help_requested' ? '#fff5f5' : '#f8fafc', borderRadius: 10, border: `1.5px solid ${p?.status === 'completed' ? '#bbf7d0' : p?.status === 'help_requested' ? '#fca5a5' : C.border}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{u?.name || '—'}</span>
                              <span style={{ fontSize: 15 }}>{pi.icon}</span>
                            </div>
                            <div style={{ fontSize: 11, color: pi.color, fontWeight: 600 }}>{pi.label}</div>
                            {p?.help_note && <div style={{ fontSize: 10, color: '#ef4444', marginTop: 3 }}>🆘 {p.help_note.slice(0, 40)}</div>}
                            {p?.completed_at && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{formatDate(p.completed_at)}</div>}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}

      {/* ══════════════ TAB: 任務統計 ══════════════ */}
      {activeTab === 'analytics' && canAssign && (
        <>
          {/* KPI */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { icon: '📋', label: '進行中任務', value: tasks.length, color: C.accent, bg: C.accentDim, sub: `共指派 ${adminStats.totalAssigned} 人次` },
              { icon: '✅', label: '整體完成率', value: `${adminStats.completionRate}%`, color: '#22c55e', bg: '#dcfce7', sub: `${adminStats.completedAll} 筆已完成` },
              { icon: '⚠️', label: '逾期任務', value: adminStats.overdueTasks.length, color: adminStats.overdueTasks.length > 0 ? '#ef4444' : C.muted, bg: adminStats.overdueTasks.length > 0 ? '#fee2e2' : '#f1f5f9', sub: '超過截止日期' },
              { icon: '🆘', label: '待協助', value: adminStats.helpReqs.length, color: adminStats.helpReqs.length > 0 ? '#ef4444' : C.muted, bg: adminStats.helpReqs.length > 0 ? '#fee2e2' : '#f1f5f9', sub: '需要管理員介入' },
              { icon: '📦', label: '累計封存', value: archived.length, color: C.muted, bg: '#f1f5f9', sub: '已結束任務' },
            ].map(k => (
              <div key={k.label} style={{ background: k.bg, border: `1.5px solid ${k.color}33`, borderRadius: 14, padding: '14px 15px' }}>
                <div style={{ fontSize: 20 }}>{k.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: k.color, lineHeight: 1.1, marginTop: 4 }}>{k.value}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: k.color, marginTop: 4 }}>{k.label}</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>

            {/* User completion ranking */}
            <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${C.border}`, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>🏅 員工完成率排行</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>依任務完成率排序</div>
                </div>
                <button style={{ ...st.btn('default'), padding: '5px 10px', fontSize: 11 }}
                  onClick={() => exportCSV('任務完成率.csv', ['排名','員工','完成數','總指派','完成率','求助次數'],
                    analytics.userRanking.map((u,i) => [i+1, u.name, u.completed, u.total, `${u.rate}%`, u.help])
                  )}>📥</button>
              </div>
              {analytics.userRanking.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 13, padding: '16px 0' }}>尚無資料</div>
              ) : analytics.userRanking.slice(0, 10).map((u, i) => (
                <div key={u.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: i < 3 ? '#f59e0b' : C.muted, fontWeight: i < 3 ? 900 : 400, width: 20, textAlign: 'right', flexShrink: 0 }}>#{i+1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{u.name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: u.rate >= 80 ? '#22c55e' : u.rate >= 50 ? '#f59e0b' : '#ef4444' }}>{u.rate}%</span>
                        <span style={{ fontSize: 10, color: C.muted }}>({u.completed}/{u.total})</span>
                        {u.help > 0 && <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 700 }}>🆘{u.help}</span>}
                      </div>
                    </div>
                    <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6 }}>
                      <div style={{ width: `${u.rate}%`, height: '100%', background: u.rate >= 80 ? '#22c55e' : u.rate >= 50 ? '#f59e0b' : '#ef4444', borderRadius: 4 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* By category */}
            <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${C.border}`, padding: '16px 18px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 14 }}>📂 任務分類統計</div>
              {analytics.byCat.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 13 }}>尚無資料</div>
              ) : analytics.byCat.map(c => {
                const maxCount = Math.max(...analytics.byCat.map(x => x.count), 1)
                return (
                  <div key={c.label} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, color: C.text }}>{c.icon} {c.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: c.color }}>{c.count}</span>
                    </div>
                    <div style={{ background: '#f1f5f9', borderRadius: 4, height: 8 }}>
                      <div style={{ width: `${(c.count / maxCount) * 100}%`, height: '100%', background: c.color, borderRadius: 4, opacity: 0.85 }} />
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Overdue task list */}
            {analytics.overdueTasks.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '2px solid #fed7aa', padding: '16px 18px' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#f97316', marginBottom: 14 }}>⏰ 逾期任務清單</div>
                {analytics.overdueTasks.map(t => {
                  const tp       = progress.filter(p => p.task_id === t.id)
                  const doneN    = tp.filter(p => p.status === 'completed').length
                  const totalN   = t.assigned_to?.length || 0
                  const overDays = Math.abs(daysUntil(t.due_date))
                  return (
                    <div key={t.id} style={{ padding: '10px 12px', background: '#fff7ed', borderRadius: 10, marginBottom: 8, border: '1.5px solid #fed7aa' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t.title}</div>
                          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                            {catInfo(t.category).label} · 建立：{t.assigned_by}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: '#ef4444' }}>逾期 {overDays} 天</div>
                          <div style={{ fontSize: 10, color: C.muted }}>{doneN}/{totalN} 完成</div>
                        </div>
                      </div>
                      <div style={{ background: '#fff', borderRadius: 4, height: 6, marginTop: 6 }}>
                        <div style={{ width: `${totalN > 0 ? Math.round(doneN/totalN*100) : 0}%`, height: '100%', background: '#22c55e', borderRadius: 4 }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

          </div>
        </>
      )}

      {/* ── Help request modal ────────────────────────────────────────────────── */}
      {helpModal && (
        <Modal title={_('taskHelpTitle')} onClose={() => { setHelpModal(null); setHelpNote('') }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{helpModal.title}</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>{_('taskHelpDesc')}</div>
          <label style={st.label}>{_('taskHelpProblem')}</label>
          <textarea style={{ ...st.input, minHeight: 90, marginBottom: 16 }} value={helpNote}
            placeholder="例：不知道如何操作機台、材料不足、需要額外人手..."
            onChange={e => setHelpNote(e.target.value)} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn('danger'), flex: 1, padding: '13px' }} onClick={handleRequestHelp} disabled={saving}>
              {saving ? _('submittingBtn') : _('taskSubmitHelp')}
            </button>
            <button style={{ ...st.btn('default'), padding: '13px 18px' }} onClick={() => { setHelpModal(null); setHelpNote('') }}>{_('cancel')}</button>
          </div>
        </Modal>
      )}
    </div>
  )
}
